/**
 * DEMO ONLY — validation for an ordered band set.
 * Mirrors price_book_update/plans_and_actions/07-design-dynamic-slabs.md §12.
 *
 * band: { minQty: number, maxQty: number|null, label: string }
 *
 * `options.requireContiguous` (default false): when true, bands must have no
 * gaps (used for the global set). Custom sets allow gaps.
 */

export function validateBandSet(bands, options = {}) {
  const { requireContiguous = false } = options;
  const errors = [];

  if (!Array.isArray(bands) || bands.length === 0) {
    return ["Add at least one band."];
  }

  const ordered = [...bands].sort((a, b) => a.minQty - b.minQty);

  // one open-ended top band, and it must be the highest
  const openEnded = ordered.filter((b) => b.maxQty === null);
  if (openEnded.length === 0) {
    errors.push("The highest band must be open-ended (no max quantity).");
  } else if (openEnded.length > 1) {
    errors.push("Only the highest band can be open-ended.");
  } else if (ordered[ordered.length - 1].maxQty !== null) {
    errors.push("The open-ended band must be the highest band.");
  }

  const labels = new Set();
  ordered.forEach((b, i) => {
    if (!b.label || !b.label.trim()) {
      errors.push(`Band ${i + 1}: label is required.`);
    } else if (b.label.length > 50) {
      errors.push(`Band ${i + 1}: label must be 50 characters or fewer.`);
    } else if (labels.has(b.label.trim())) {
      errors.push(`Band ${i + 1}: label "${b.label.trim()}" is used twice.`);
    } else {
      labels.add(b.label.trim());
    }

    if (!Number.isInteger(b.minQty) || b.minQty < 1) {
      errors.push(`Band ${i + 1}: minimum quantity must be a whole number of 1 or more.`);
    }
    if (b.maxQty !== null && (!Number.isInteger(b.maxQty) || b.maxQty < b.minQty)) {
      errors.push(`Band ${i + 1}: maximum quantity must be a whole number not less than the minimum.`);
    }

    if (i > 0) {
      const prev = ordered[i - 1];
      if (prev.maxQty === null) {
        // already reported "open-ended must be highest"
      } else if (b.minQty <= prev.maxQty) {
        errors.push(`Band ${i + 1} ("${b.label}") overlaps the previous band.`);
      } else if (requireContiguous && b.minQty !== prev.maxQty + 1) {
        errors.push(
          `Band ${i + 1} ("${b.label}") leaves a gap after ${prev.maxQty}. The global set must have no gaps.`
        );
      }
    }
  });

  return errors;
}

export function isBandSetValid(bands, options) {
  return validateBandSet(bands, options).length === 0;
}
